import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PublicIcon from "@mui/icons-material/Public";
import RedditIcon from "@mui/icons-material/Reddit";
import ForumIcon from "@mui/icons-material/Forum";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { alpha } from "@mui/material/styles";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
  Legend,
} from "recharts";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { useThemeSwitcher } from "../../theme/ThemeContext";
import { useModules } from "../../ModuleContext";
import getEnv from "../../config";
import { createSocket } from "../../services/socket";
import WidgetCard from "../common/WidgetCard";
import { getAllResultsCount } from "../../services/generic_services";
import {
  getRecentDocuments,
  getAttributeStats,
  getCategoryStats,
  getRecentEvents,
  getTimeline,
  getTags,
  getGeoIps,
  getFeeds,
  getSightings,
  getThreatLevels,
  getActionableIocs,
  getThreatActors,
  // getMitreTechniques,
  getDashboardLayout,
  saveDashboardLayout,
} from "../../services/dashboard_services";
import { getCrawlTasks } from "../../services/web-services";
import { getRedditTasks } from "../../services/reddit_services";
import { getMastodonTasks } from "../../services/mastodon_services";

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480 };
const COLS = { lg: 12, md: 10, sm: 6, xs: 4 };

// Where every widget sits, in one place. `lg` is the authored 12-column layout;
// the narrower breakpoints only declare a width, and the packer below flows the
// widgets in this order, wrapping when a row is full. Previously each widget
// carried its own `data-grid`, which gave a single 12-column layout that did not
// fit the 10/6/4-column breakpoints — widgets were pushed around and left holes.
const WIDGETS = [
  { i: "totalDocs", lg: [0, 0, 3, 4], w: { md: 5, sm: 3, xs: 4 }, h: 4 },
  { i: "totalMastodon", lg: [3, 0, 3, 4], w: { md: 5, sm: 3, xs: 4 }, h: 4 },
  { i: "totalReddit", lg: [6, 0, 3, 4], w: { md: 5, sm: 3, xs: 4 }, h: 4 },
  { i: "activeTasks", lg: [9, 0, 3, 4], w: { md: 5, sm: 3, xs: 4 }, h: 4 },

  { i: "threatMap", lg: [0, 4, 8, 12], w: { md: 10, sm: 6, xs: 4 }, h: 12 },
  { i: "attrDist", lg: [8, 4, 4, 12], w: { md: 10, sm: 6, xs: 4 }, h: 12 },

  { i: "eventTimeline", lg: [0, 16, 8, 10], w: { md: 10, sm: 6, xs: 4 }, h: 10 },
  { i: "recentEvents", lg: [8, 16, 4, 10], w: { md: 10, sm: 6, xs: 4 }, h: 10 },

  { i: "tagCloud", lg: [0, 26, 4, 8], w: { md: 5, sm: 6, xs: 4 }, h: 8 },
  { i: "taskStatus", lg: [4, 26, 4, 8], w: { md: 5, sm: 6, xs: 4 }, h: 8 },
  { i: "liveFeed", lg: [8, 26, 4, 8], w: { md: 10, sm: 6, xs: 4 }, h: 8 },

  { i: "mispFeeds", lg: [0, 34, 4, 8], w: { md: 5, sm: 6, xs: 4 }, h: 8 },
  { i: "sightingActivity", lg: [4, 34, 4, 8], w: { md: 5, sm: 6, xs: 4 }, h: 8 },
  { i: "threatCategories", lg: [8, 34, 4, 8], w: { md: 10, sm: 6, xs: 4 }, h: 8 },

  // mitreTechniques used to sit at x:0 y:42 w:8, overlapping threatLevels and
  // threatActors on the same row. It gets the full width and its own row now.
  { i: "mitreTechniques", lg: [0, 42, 12, 10], w: { md: 10, sm: 6, xs: 4 }, h: 10 },
  { i: "threatLevels", lg: [0, 52, 4, 10], w: { md: 5, sm: 6, xs: 4 }, h: 10 },
  { i: "threatActors", lg: [4, 52, 4, 10], w: { md: 5, sm: 6, xs: 4 }, h: 10 },
  { i: "actionableIocs", lg: [8, 52, 4, 10], w: { md: 10, sm: 6, xs: 4 }, h: 10 },
];

// Flow the widgets left to right at the given column count, wrapping on overflow.
const packLayout = (breakpoint, cols) => {
  let x = 0;
  let y = 0;
  let rowHeight = 0;

  return WIDGETS.map(({ i, w, h }) => {
    const width = Math.min(w[breakpoint], cols);
    if (x + width > cols) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    const item = { i, x, y, w: width, h };
    x += width;
    rowHeight = Math.max(rowHeight, h);
    return item;
  });
};

const DEFAULT_LAYOUTS = {
  lg: WIDGETS.map(({ i, lg: [x, y, w, h] }) => ({ i, x, y, w, h })),
  md: packLayout("md", COLS.md),
  sm: packLayout("sm", COLS.sm),
  xs: packLayout("xs", COLS.xs),
};

// Bumped when WIDGETS changes shape, so stale saved layouts are discarded.
const LAYOUT_STORAGE_KEY = "dashboard.layouts.v1";

// The server holds the layout that follows the user between machines; this is
// only a local cache, so the last arrangement paints immediately on reload
// instead of flashing the default while the request is in flight.
const readCachedLayouts = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(LAYOUT_STORAGE_KEY));
    return saved && typeof saved === "object" ? saved : null;
  } catch {
    // Private windows and blocked storage both throw; the defaults still work.
    return null;
  }
};

const writeCachedLayouts = (layouts) => {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layouts));
  } catch {
    // Not being able to cache the layout is not worth breaking the page over.
  }
};

// Dragging settles into a burst of layout changes; only the last one is worth a PUT.
const LAYOUT_SAVE_DEBOUNCE_MS = 800;

// Crawl tasks from every enabled crawler; each source names its states slightly differently
const TASK_SOURCES = [
  { module: "crawlers", fetch: getCrawlTasks },
  { module: "social_crawlers", fetch: getRedditTasks },
  { module: "social_crawlers", fetch: getMastodonTasks },
];
const TASK_STATE_LABELS = {
  PENDING: "Queued",
  RUNNING: "Running",
  SUCCESS: "Completed",
  FINISHED: "Completed",
  FAILED: "Failed",
  FAILURE: "Failed",
  REVOKED: "Stopped",
  CANCELED: "Stopped",
  KILLED: "Stopped",
};
const TASK_STATE_ORDER = ["Running", "Queued", "Completed", "Failed", "Stopped", "Other"];
const TASK_COLORS = {
  Running: "#4b8085",
  Queued: "#ffa726",
  Completed: "#66bb6a",
  Failed: "#ef5350",
  Stopped: "#9e9e9e",
  Other: "#78909c",
};
const taskLabel = (state) => TASK_STATE_LABELS[(state || "").toUpperCase()] || "Other";

// Fails only if every enabled source fails, so one crawler being down doesn't blank the widget
const fetchAllTasks = async (modules) => {
  const sources = TASK_SOURCES.filter((s) => modules[s.module]);
  const results = await Promise.allSettled(sources.map((s) => s.fetch()));
  if (sources.length && results.every((r) => r.status === "rejected")) throw results[0].reason;
  return results.flatMap((r) => (r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []));
};
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const THREAT_LEVEL_COLORS = {
  High: "#ef5350",
  Medium: "#ffa726",
  Low: "#66bb6a",
  Undefined: "#9e9e9e",
};

// Recent Activity widget: one entry per source, colors match the KPI cards above it
const ACTIVITY_LIMIT = 10;
const ACTIVITY_SOURCES = {
  web: { color: "#4FB6BC", Icon: PublicIcon },
  reddit: { color: "#FF4500", Icon: RedditIcon },
  mastodon: { color: "#6364FF", Icon: ForumIcon },
};

// Mongo dates arrive via bson.json_util as {"$date": iso | {"$numberLong": ms}} or a plain string
const toDate = (v) => {
  const raw = v && typeof v === "object" && "$date" in v ? v.$date : v;
  const val = raw && typeof raw === "object" && "$numberLong" in raw ? Number(raw.$numberLong) : raw;
  const d = val ? new Date(val) : null;
  return d && !isNaN(d) ? d : null;
};

const formatActivityTime = (d) => {
  if (!d) return "";
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toDateString() === new Date().toDateString()
    ? time
    : `${d.toLocaleDateString([], { day: "2-digit", month: "2-digit" })} ${time}`;
};

const MISP_CALLS = [
  { key: "attributeStats", fn: getAttributeStats },
  { key: "categoryStats", fn: getCategoryStats },
  { key: "recentEvents", fn: getRecentEvents },
  { key: "timeline", fn: getTimeline },
  { key: "tags", fn: getTags },
  { key: "geoIps", fn: getGeoIps },
  { key: "feeds", fn: getFeeds },
  { key: "sightings", fn: getSightings },
  { key: "threatLevels", fn: getThreatLevels },
  { key: "actionableIocs", fn: getActionableIocs },
  { key: "threatActors", fn: getThreatActors },
  // { key: "mitreTechniques", fn: getMitreTechniques }, // widget hidden, see Row 6
];

const Loading = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      minHeight: 80,
    }}
  >
    <CircularProgress size={28} sx={{ color: "#4b8085" }} />
  </Box>
);

const Unavailable = ({ msg = "Data unavailable" }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      minHeight: 80,
    }}
  >
    <Typography variant="body2" color="text.disabled">
      {msg}
    </Typography>
  </Box>
);

const SummaryCard = ({ label, value, color, icon, loading, error }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      height: "100%",
      borderLeft: `3px solid ${color}`,
      pl: 2,
      py: 0.5,
    }}
  >
    {loading ? (
      <Loading />
    ) : error ? (
      <Unavailable />
    ) : (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: alpha(color, 0.12),
                color,
              }}
            >
              {icon}
            </Box>
          )}
          <Typography variant="subtitle2" color="text.secondary">
            {label}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ color: "text.primary", lineHeight: 1.1, mt: 0.5 }}>
          {value ?? 0}
        </Typography>
      </>
    )}
  </Box>
);

const Dashboard = () => {
  // Whatever the user last dragged into place, falling back to the authored layout.
  const [layouts, setLayouts] = useState(() => readCachedLayouts() || DEFAULT_LAYOUTS);

  // The grid reports a layout change on mount too. Saving that would overwrite the
  // user's stored arrangement with the default before it has even been fetched.
  const layoutHydratedRef = useRef(false);
  const layoutSaveTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    getDashboardLayout()
      .then((saved) => {
        if (cancelled) return;
        if (saved && typeof saved === "object" && Object.keys(saved).length) {
          setLayouts(saved);
          writeCachedLayouts(saved);
        }
      })
      .catch(() => {
        // Keep the cached or default layout; the dashboard still works.
      })
      .finally(() => {
        if (!cancelled) layoutHydratedRef.current = true;
      });

    return () => {
      cancelled = true;
      clearTimeout(layoutSaveTimerRef.current);
    };
  }, []);

  const handleLayoutChange = useCallback((_current, allLayouts) => {
    setLayouts(allLayouts);
    writeCachedLayouts(allLayouts);

    if (!layoutHydratedRef.current) return;

    clearTimeout(layoutSaveTimerRef.current);
    layoutSaveTimerRef.current = setTimeout(() => {
      saveDashboardLayout(allLayouts).catch(() => {
        // The local cache still has it; a later drag will retry the save.
      });
    }, LAYOUT_SAVE_DEBOUNCE_MS);
  }, []);

  const [loading, setLoading] = useState(true);
  const [resultsCount, setResultsCount] = useState(null);
  const [recentDocs, setRecentDocs] = useState(null);
  const [crawlTasks, setCrawlTasks] = useState(null);
  const [mispData, setMispData] = useState({});
  const [alertFeed, setAlertFeed] = useState([]);
  const [errors, setErrors] = useState({});
  const [tooltipContent, setTooltipContent] = useState("");
  const { branding } = useThemeSwitcher();
  const { modules, settled: modulesSettled } = useModules();
  const modulesRef = useRef(modules); // read by the long-lived socket handlers
  modulesRef.current = modules;
  const COLORS = branding.chartColors;

  const fetchKey = useMemo(
    () => `${modulesSettled}|${modules.crawlers}|${modules.social_crawlers}|${modules.cti_gathering}`,
    [modulesSettled, modules.crawlers, modules.social_crawlers, modules.cti_gathering]
  );

  useEffect(() => {
    if (!modulesSettled) return;

    let stale = false;

    const fetchAll = async () => {
      setLoading(true);
      setMispData({});
      setErrors({});

      const dataPromises = [];
      if (modules.crawlers || modules.social_crawlers) {
        dataPromises.push({ key: "resultsCount", promise: getAllResultsCount(), setter: setResultsCount });
        dataPromises.push({ key: "recentDocs", promise: getRecentDocuments(5), setter: setRecentDocs });
      }
      if (modules.crawlers || modules.social_crawlers) {
        dataPromises.push({ key: "crawlTasks", promise: fetchAllTasks(modules), setter: setCrawlTasks });
      }

      const mispPromises = [];
      if (modules.cti_gathering) {
        MISP_CALLS.forEach(({ key, fn }) => {
          mispPromises.push(
            fn()
              .then((data) => {
                if (!stale) setMispData((prev) => ({ ...prev, [key]: data }));
              })
              .catch((err) => {
                if (!stale) setErrors((prev) => ({ ...prev, [key]: true }));
                console.error(`Failed to fetch ${key}:`, err);
              })
          );
        });
      }

      if (dataPromises.length === 0 && mispPromises.length === 0) {
        setLoading(false);
        return;
      }


      const dataResults = await Promise.allSettled(dataPromises.map((e) => e.promise));
      if (stale) return;

      dataResults.forEach((result, i) => {
        if (result.status === "fulfilled") {
          dataPromises[i].setter(result.value);
        } else {
          setErrors((prev) => ({ ...prev, [dataPromises[i].key]: true }));
          console.error(`Failed to fetch ${dataPromises[i].key}:`, result.reason);
        }
      });

      await Promise.allSettled(mispPromises);
      if (!stale) setLoading(false);
    };

    fetchAll();
    return () => { stale = true; };
  }, [fetchKey]);


  useEffect(() => {
    const apiUrl = getEnv("REACT_APP_API_URL");
    if (!apiUrl) return;

    const socket = createSocket();

    const push = (type, message) =>
      setAlertFeed((prev) => [{ time: new Date(), type, message, live: true }, ...prev].slice(0, ACTIVITY_LIMIT));

    socket.on("new_document", (d) => push("web", d?.title || d?.url || "New document ingested"));
    socket.on("reddit_submission", (d) => push("reddit", d?.subreddit ? `New post in r/${d.subreddit}` : "New Reddit post"));
    socket.on("mastodon_post", (d) => push("mastodon", d?.hashtag ? `New post in #${d.hashtag}` : "New Mastodon post"));

    // A task changed state: refetch the task list (debounced, a crawl start fires several events)
    let refreshTimer;
    const refreshTasks = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        fetchAllTasks(modulesRef.current).then(setCrawlTasks).catch(() => {});
      }, 1000);
    };
    ["state_update", "reddit_state_update", "mastodon_state_update"].forEach((evt) => socket.on(evt, refreshTasks));

    return () => {
      clearTimeout(refreshTimer);
      socket.disconnect();
    };
  }, []);

  // Live socket events first, then the latest stored web documents so the widget isn't empty on load
  const activity = useMemo(() => {
    const stored = (Array.isArray(recentDocs) ? recentDocs : []).map((doc) => ({
      time: toDate(doc.created_at),
      type: "web",
      message: doc.title || doc.url || "Web document",
      live: false,
    }));
    return [...alertFeed, ...stored].slice(0, ACTIVITY_LIMIT);
  }, [alertFeed, recentDocs]);

  const activeTasksCount = useMemo(
    () => Array.isArray(crawlTasks) ? crawlTasks.filter((t) => taskLabel(t.state) === "Running").length : 0,
    [crawlTasks]
  );

  const taskStateData = useMemo(() => {
    if (!Array.isArray(crawlTasks)) return [];
    const counts = {};
    crawlTasks.forEach((t) => {
      const label = taskLabel(t.state);
      counts[label] = (counts[label] || 0) + 1;
    });
    return TASK_STATE_ORDER.filter((l) => counts[l]).map((name) => ({ name, value: counts[name] }));
  }, [crawlTasks]);

  const topAttributes = useMemo(() => {
    const attributeStats = mispData.attributeStats;
    if (!attributeStats || typeof attributeStats !== "object") return [];
    if (Array.isArray(attributeStats)) return attributeStats.slice(0, 10);
    return Object.entries(attributeStats)
      .map(([type, count]) => ({ type, count: Number(count) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [mispData.attributeStats]);

  const categoryData = useMemo(() => {
    const categoryStats = mispData.categoryStats;
    if (!categoryStats || typeof categoryStats !== "object") return [];
    const rows = Array.isArray(categoryStats)
      ? categoryStats.map((c) => ({ category: c.category, count: Number(c.count) }))
      : Object.entries(categoryStats).map(([category, count]) => ({ category, count: Number(count) }));
    return rows.sort((a, b) => b.count - a.count);
  }, [mispData.categoryStats]);

  const categoryTotal = useMemo(
    () => categoryData.reduce((sum, c) => sum + (c.count || 0), 0),
    [categoryData]
  );

  return (
    <>
      <ResponsiveGridLayout
        className="layout"
        rowHeight={30}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        layouts={layouts}
        onLayoutChange={handleLayoutChange}
        // Without this the grid treats a click on a widget's info button as the
        // start of a drag, so the button never receives the click.
        draggableCancel=".widget-info-button"
      >
        {/* Row 1: Summary Cards */}
        {/* KPI: total web documents collected by the crawlers */}
        {modules.crawlers && <div key="totalDocs">
          <WidgetCard description="Total web documents collected by the crawlers.">
            <SummaryCard
              label="Total Documents"
              value={resultsCount?.documents}
              color="#4FB6BC"
              icon={<PublicIcon fontSize="small" />}
              loading={loading}
              error={errors.resultsCount}
            />
          </WidgetCard>
        </div>}

        {/* Twitter widget hidden — kept for future re-enable.
        {modules.social_crawlers && <div key="totalTweets">
          <WidgetCard>
            <SummaryCard
              label="Total Tweets"
              value={resultsCount?.tweets}
              color={COLORS[1]}
              loading={loading}
              error={errors.resultsCount}
            />
          </WidgetCard>
        </div>}
        */}

        {/* KPI: total Reddit posts collected */}
        {modules.social_crawlers && <div key="totalReddit">
          <WidgetCard description="Total Reddit posts collected by the social crawlers.">
            <SummaryCard
              label="Total Reddit Posts"
              value={resultsCount?.submissions}
              color="#FF4500"
              icon={<RedditIcon fontSize="small" />}
              loading={loading}
              error={errors.resultsCount}
            />
          </WidgetCard>
        </div>}

        {/* KPI: total Mastodon posts collected */}
        {modules.social_crawlers && <div key="totalMastodon">
          <WidgetCard description="Total Mastodon posts collected by the social crawlers.">
            <SummaryCard
              label="Total Mastodon Posts"
              value={resultsCount?.mastodon}
              color="#6364FF"
              icon={<ForumIcon fontSize="small" />}
              loading={loading}
              error={errors.resultsCount}
            />
          </WidgetCard>
        </div>}

        {/* Telegram widget hidden — kept for future re-enable.
        {modules.social_crawlers && <div key="totalTelegram">
          <WidgetCard>
            <SummaryCard
              label="Total Telegram Messages"
              value={resultsCount?.telegram}
              color={COLORS[3] || COLORS[0]}
              loading={loading}
              error={errors.resultsCount}
            />
          </WidgetCard>
        </div>}
        */}

        {/* KPI: crawl tasks (web, Reddit, Mastodon) currently running */}
        {(modules.crawlers || modules.social_crawlers) && <div key="activeTasks">
          <WidgetCard description="Number of web, Reddit and Mastodon crawl tasks currently running.">
            <SummaryCard
              label="Active Tasks"
              value={activeTasksCount}
              color="#66bb6a"
              icon={<PlayCircleOutlineIcon fontSize="small" />}
              loading={loading}
              error={errors.crawlTasks}
            />
          </WidgetCard>
        </div>}

        {/* Row 2: Map + Attribute Distribution */}
        {/* World map plotting geolocated IPs from MISP attributes (last 30d) */}
        {modules.cti_gathering && <div key="threatMap">
          <WidgetCard title="Global Threat Map" description="Geolocated IPs from MISP attributes over the last 30 days, plotted on a world map.">
            {!mispData.geoIps && !errors.geoIps ? (
              <Loading />
            ) : errors.geoIps ? (
              <Unavailable />
            ) : (
              <div style={{ width: "100%", height: "100%" }}>
                <ComposableMap
                  projectionConfig={{ scale: 250 }}
                  style={{ width: "100%", height: "100%" }}
                >
                  <ZoomableGroup>
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={COLORS[0]}
                            stroke="#ffffff22"
                            style={{
                              default: { outline: "none" },
                              hover: { fill: COLORS[1], outline: "none" },
                              pressed: { fill: COLORS[3], outline: "none" },
                            }}
                          />
                        ))
                      }
                    </Geographies>
                    {Array.isArray(mispData.geoIps) &&
                      mispData.geoIps.map((ip, idx) => {
                        if (!ip.lat || !ip.lng) return null;
                        const density = ip.count || 1;
                        const radius = Math.min(
                          2 + Math.log2(density + 1) * 2,
                          12
                        );
                        return (
                          <Marker
                            key={idx}
                            coordinates={[ip.lng, ip.lat]}
                            onMouseEnter={() =>
                              setTooltipContent(
                                `${ip.ip || ""} - ${ip.city || ""}, ${ip.country || ""} (${density})`
                              )
                            }
                            onMouseLeave={() => setTooltipContent("")}
                          >
                            <circle
                              r={radius}
                              fill="rgba(239,83,80,0.7)"
                              stroke="#ef5350"
                              strokeWidth={0.5}
                            />
                          </Marker>
                        );
                      })}
                  </ZoomableGroup>
                </ComposableMap>
                {tooltipContent && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: 8,
                      bgcolor: "rgba(0,0,0,0.75)",
                      color: "#fff",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: 12,
                    }}
                  >
                    {tooltipContent}
                  </Box>
                )}
                {Array.isArray(mispData.geoIps) && mispData.geoIps.length === 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      fontSize: 13,
                    }}
                  >
                    No geolocated IPs in the events yet
                  </Box>
                )}
              </div>
            )}
          </WidgetCard>
        </div>}

        {/* Top 10 MISP attribute types by count (which IoC types dominate) */}
        {modules.cti_gathering && <div key="attrDist">
          <WidgetCard title="Attribute Types Distribution" description="The top 10 MISP attribute types by count which indicator types dominate.">
            {!mispData.attributeStats && !errors.attributeStats ? (
              <Loading />
            ) : errors.attributeStats ? (
              <Unavailable />
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={topAttributes}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="type"
                    type="category"
                    tick={{ fontSize: 11 }}
                    width={75}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill={COLORS[0]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </WidgetCard>
        </div>}

        {/* Row 3: Timeline + Recent Events */}
        {/* MISP events created per day over the last 30 days (activity trend) */}
        {modules.cti_gathering && <div key="eventTimeline">
          <WidgetCard title="Event Timeline" description="MISP events created per day over the last 30 days.">
            {!mispData.timeline && !errors.timeline ? (
              <Loading />
            ) : errors.timeline ? (
              <Unavailable />
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <AreaChart
                  data={Array.isArray(mispData.timeline) ? mispData.timeline : []}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="gradientTeal"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor={COLORS[0]}
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={COLORS[0]}
                    fill="url(#gradientTeal)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </WidgetCard>
        </div>}

        {/* Most recent published MISP events with their tags and attribute count */}
        {modules.cti_gathering && <div key="recentEvents">
          <WidgetCard title="Recent MISP Events" description="The most recent published MISP events, with their tags and attribute counts.">
            {!mispData.recentEvents && !errors.recentEvents ? (
              <Loading />
            ) : errors.recentEvents ? (
              <Unavailable />
            ) : (
              <Box sx={{ overflow: "auto", height: "90%", pr: 1 }}>
                {Array.isArray(mispData.recentEvents) && mispData.recentEvents.length > 0 ? (
                  mispData.recentEvents.map((evt, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{ p: 1, mb: 1, borderLeft: `3px solid ${COLORS[0]}` }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {evt.info || "Untitled Event"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evt.date || ""} | {evt.orgc_name || evt.org || ""}
                      </Typography>
                      <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {Array.isArray(evt.tags) &&
                          evt.tags.slice(0, 5).map((tag, ti) => (
                            <Chip
                              key={ti}
                              label={tag.name || tag}
                              size="small"
                              sx={{
                                fontSize: 10,
                                height: 20,
                                backgroundColor:
                                  tag.colour || tag.color || "#6b8a89",
                                color: "#fff",
                              }}
                            />
                          ))}
                        {evt.attribute_count != null && (
                          <Chip
                            label={`${evt.attribute_count} attrs`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10, height: 20 }}
                          />
                        )}
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Unavailable msg="No recent events" />
                )}
              </Box>
            )}
          </WidgetCard>
        </div>}

        {/* Row 4: Tag Cloud + Task Status + Live Feed */}
        {/* Most-used MISP tags, sized by frequency (tag cloud) */}
        {modules.cti_gathering && <div key="tagCloud">
          <WidgetCard title="Tag Cloud" description="The most-used MISP tags, sized by how often they appear.">
            {!mispData.tags && !errors.tags ? (
              <Loading />
            ) : errors.tags ? (
              <Unavailable />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  overflow: "auto",
                  height: "90%",
                  alignContent: "flex-start",
                }}
              >
                {Array.isArray(mispData.tags) && mispData.tags.length > 0 ? (
                  mispData.tags.map((tag, idx) => {
                    const count = tag.count || tag.attribute_count || 1;
                    const fontSize = Math.min(10 + Math.log2(count + 1) * 2, 18);
                    return (
                      <Chip
                        key={idx}
                        label={`${tag.name || tag.tag || ""} (${count})`}
                        size="small"
                        sx={{
                          fontSize,
                          height: "auto",
                          py: 0.3,
                          backgroundColor:
                            tag.colour || tag.color || COLORS[idx % COLORS.length],
                          color: "#fff",
                        }}
                      />
                    );
                  })
                ) : (
                  <Unavailable msg="No tags" />
                )}
              </Box>
            )}
          </WidgetCard>
        </div>}

        {/* Crawl tasks from all crawlers (web, Reddit, Mastodon) broken down by state */}
        {(modules.crawlers || modules.social_crawlers) && <div key="taskStatus">
          <WidgetCard title="Task Status" description="Web, Reddit and Mastodon crawl tasks broken down by state (running, completed, failed…).">
            {loading ? (
              <Loading />
            ) : errors.crawlTasks ? (
              <Unavailable />
            ) : taskStateData.length === 0 ? (
              <Unavailable msg="No crawl tasks yet" />
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={taskStateData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                  >
                    {taskStateData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          TASK_COLORS[entry.name] ||
                          COLORS[index % COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={(value, entry) => `${value} (${entry.payload.value})`} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </WidgetCard>
        </div>}

        {/* Latest ingested documents/posts: stored web documents on load, then live socket.io events */}
        <div key="liveFeed">
          <WidgetCard
            title="Recent Activity"
            description="The latest collected web documents, plus new documents and posts as the crawlers bring them in."
          >
            <Box sx={{ overflow: "auto", height: "90%" }}>
              {activity.length > 0 ? (
                activity.map((a, i) => {
                  const { color, Icon } = ACTIVITY_SOURCES[a.type] || ACTIVITY_SOURCES.web;
                  return (
                    <Box
                      key={i}
                      title={a.message}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                        py: 0.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color, flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 40, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
                      >
                        {formatActivityTime(a.time)}
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {a.message}
                      </Typography>
                      {a.live && (
                        <Chip
                          label="new"
                          size="small"
                          sx={{ height: 18, fontSize: 10, bgcolor: alpha(color, 0.15), color: "text.primary" }}
                        />
                      )}
                    </Box>
                  );
                })
              ) : (
                <Unavailable msg="No activity yet" />
              )}
            </Box>
          </WidgetCard>
        </div>

        {/* Row 5: Feeds + Sightings + Categories */}
        {/* Configured MISP feeds and whether each is enabled */}
        {modules.cti_gathering && <div key="mispFeeds">
          <WidgetCard title="MISP Feeds" description="The configured MISP feeds and whether each one is enabled.">
            {!mispData.feeds && !errors.feeds ? (
              <Loading />
            ) : errors.feeds ? (
              <Unavailable />
            ) : (
              <Box sx={{ overflow: "auto", height: "90%" }}>
                {Array.isArray(mispData.feeds) && mispData.feeds.length > 0 ? (
                  mispData.feeds.map((feed, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 1,
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <FiberManualRecordIcon
                        sx={{
                          fontSize: 12,
                          color: feed.enabled ? "#66bb6a" : "#ef5350",
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {feed.name || "Unnamed Feed"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {feed.provider || ""}
                        </Typography>
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Unavailable msg="No MISP feeds configured" />
                )}
              </Box>
            )}
          </WidgetCard>
        </div>}

        {/* MISP sightings recorded per day (last 30d) — indicator activity */}
        {modules.cti_gathering && <div key="sightingActivity">
          <WidgetCard title="Sighting Activity" description="MISP sightings recorded per day over the last 30 days.">
            {!mispData.sightings && !errors.sightings ? (
              <Loading />
            ) : errors.sightings ? (
              <Unavailable />
            ) : Array.isArray(mispData.sightings) && mispData.sightings.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={mispData.sightings}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill={COLORS[1]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Unavailable msg="No sightings recorded yet" />
            )}
          </WidgetCard>
        </div>}

        {/* Distribution of MISP attributes by category: ranked share bars, scrolls when there are many */}
        {modules.cti_gathering && <div key="threatCategories">
          <WidgetCard title="Threat Categories" description="Distribution of MISP attributes by category.">
            {!mispData.categoryStats && !errors.categoryStats ? (
              <Loading />
            ) : errors.categoryStats ? (
              <Unavailable />
            ) : categoryData.length > 0 ? (
              <Box sx={{ overflow: "auto", height: "90%", pr: 1 }}>
                {categoryData.map((c) => {
                  const pct = categoryTotal ? (c.count / categoryTotal) * 100 : 0;
                  const pctLabel = pct >= 1 || pct === 0 ? pct.toFixed(0) : "<1";
                  return (
                    <Box
                      key={c.category}
                      title={`${c.category}: ${c.count.toLocaleString()} (${pct.toFixed(1)}%)`}
                      sx={{ mb: 1.25 }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                        <Typography variant="body2" noWrap sx={{ color: "text.primary", minWidth: 0 }}>
                          {c.category}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
                        >
                          {c.count.toLocaleString()} · {pctLabel}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.5,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
                        }}
                      >
                        <Box
                          sx={{
                            width: `${pct}%`,
                            minWidth: c.count > 0 ? 4 : 0,
                            height: "100%",
                            borderRadius: 3,
                            // Darkest brand step lacks contrast on the dark surface
                            bgcolor: (t) => (t.palette.mode === "dark" ? COLORS[1] : COLORS[0]),
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Unavailable msg="No categories yet" />
            )}
          </WidgetCard>
        </div>}

        {/* Row 6: Threat Levels + Threat Actors + Actionable IOCs */}
        {/* Top MITRE ATT&CK techniques seen, from mitre-attack-pattern galaxy tags.
            Hidden: the tags are only added by the cti-llm LLM pipeline, so without a GPU host the widget is always empty.
        {modules.cti_gathering && <div key="mitreTechniques">
          <WidgetCard title="Top MITRE ATT&CK Techniques" description="The most frequently seen MITRE ATT&CK techniques, from galaxy tags on events.">
            {!mispData.mitreTechniques && !errors.mitreTechniques ? (
              <Loading />
            ) : errors.mitreTechniques ? (
              <Unavailable />
            ) : Array.isArray(mispData.mitreTechniques) && mispData.mitreTechniques.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={mispData.mitreTechniques}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 130, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={130} />
                  <Tooltip />
                  <Bar dataKey="count" fill={COLORS[0]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Unavailable msg="No MITRE ATT&CK techniques tagged on events yet" />
            )}
          </WidgetCard>
        </div>}
        */}

        {/* MISP events grouped by threat level (High/Medium/Low/Undefined) */}
        {modules.cti_gathering && <div key="threatLevels">
          <WidgetCard title="Threat Levels" description="MISP events grouped by threat level: High, Medium, Low or Undefined.">
            {!mispData.threatLevels && !errors.threatLevels ? (
              <Loading />
            ) : errors.threatLevels ? (
              <Unavailable />
            ) : Array.isArray(mispData.threatLevels) && mispData.threatLevels.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={mispData.threatLevels}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="level"
                  >
                    {mispData.threatLevels.map((entry, index) => (
                      <Cell
                        key={`tl-${index}`}
                        fill={THREAT_LEVEL_COLORS[entry.level] || COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Unavailable msg="No events yet" />
            )}
          </WidgetCard>
        </div>}

        {/* Row 7: Threat Actors + Actionable IOCs */}
        {/* Top threat actors seen, from misp-galaxy:threat-actor tags */}
        {modules.cti_gathering && <div key="threatActors">
          <WidgetCard title="Top Threat Actors" description="The threat actors seen most often, from MISP galaxy tags.">
            {!mispData.threatActors && !errors.threatActors ? (
              <Loading />
            ) : errors.threatActors ? (
              <Unavailable />
            ) : Array.isArray(mispData.threatActors) && mispData.threatActors.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={mispData.threatActors}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 110, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip />
                  <Bar dataKey="count" fill={COLORS[6] || "#ef5350"} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Unavailable msg="No threat actors tagged on events yet" />
            )}
          </WidgetCard>
        </div>}

        {/* Actionable IOCs (to_ids=1) — total count + breakdown by type, last 90d */}
        {modules.cti_gathering && <div key="actionableIocs">
          <WidgetCard title="Actionable IOCs (to_ids)" description="Actionable indicators (to_ids) from the last 90 days, total count and breakdown by type.">
            {!mispData.actionableIocs && !errors.actionableIocs ? (
              <Loading />
            ) : errors.actionableIocs ? (
              <Unavailable />
            ) : (
              <Box sx={{ height: "90%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
                  <Typography variant="h4" sx={{ color: "text.primary", lineHeight: 1 }}>
                    {(mispData.actionableIocs?.total ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    actionable indicators (last 90d)
                  </Typography>
                </Box>
                {Array.isArray(mispData.actionableIocs?.by_type) && mispData.actionableIocs.by_type.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={mispData.actionableIocs.by_type}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 70, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="type" type="category" tick={{ fontSize: 10 }} width={70} />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS[5] || "#66bb6a"} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Unavailable msg="No actionable (to_ids) indicators yet" />
                )}
              </Box>
            )}
          </WidgetCard>
        </div>}
      </ResponsiveGridLayout>
    </>
  );
};

export default Dashboard;
